import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { kindIsPack } from '../../lib/phaseKinds';
import { useToast } from '../../hooks/useToast';
import { enqueueAndSync } from '../../lib/portalQueue';
import {
    Button, FormField, FieldError, FormErrorBanner, NumberField, ChoiceKeys, TimePicker, Slider, Textarea,
    MobileScreen, MobileHeader, MobileActionBar, MobileCard, StepBar,
} from '../ui';
import {
    formatTime12, timeToMin, hoursBetween, fileToBase64, recapSectionsFor,
    usePhotoCapture, PhotoCaptureField, KV,
} from './portalShared';

// ---------------------------------------------------------------------------
// RecapFlow — the lead's daily recap as five screens (mobile.md, Crew
// portal 3 pick B). EVERY element of the old one-card recap is here —
// Jaylen: "keep all the elements of whatever's on the current recap form".
//
//   1 Times              billing start / end, lunch, billed-hours tile
//   2 Rating & notes     stars, crew notes, damage notes
//   3 Tip & payment      tip type + amount + per-crew splits, payment
//                        collected (method + amount), packing billing
//   4 Materials & photos materials catalog + totals, BOL / check photos,
//                        waivers on file
//   5 Review & submit    every value, then Submit recap
//
// State for all five steps lives in this ONE component (mounted for the
// whole flow; `step` is just which screen is showing). Validation is per
// step and short (feedback.md 4B). The single save happens on Submit:
// same payload, same offline queue, same dedupe key as before.
// Section composition is driven by recapSectionsFor(job.type, phase_kind).
// ---------------------------------------------------------------------------

const STEP_NAMES = { 1: 'Times', 2: 'Rating & notes', 3: 'Tip & payment', 4: 'Materials & photos', 5: 'Review & submit' };
const TIP_ITEMS = [{ value: 'none', label: 'None' }, { value: 'cash', label: 'Cash' }, { value: 'card', label: 'Card' }, { value: 'check', label: 'Check' }];
const PAY_ITEMS = [{ value: 'cash', label: 'Cash' }, { value: 'card', label: 'Card' }, { value: 'check', label: 'Check' }];
const PACKING_ITEMS = [
    { value: 'collected_today', label: 'Collected today' },
    { value: 'with_move_billing', label: "With the move's billing" },
];
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function StarRating({ value, onChange }) {
    return (
        <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Customer satisfaction rating (internal)">
            {[1, 2, 3, 4, 5].map(n => {
                const on = value >= n;
                return (
                    <button
                        key={n}
                        type="button"
                        role="radio"
                        aria-checked={value === n}
                        aria-label={`${n} star${n === 1 ? '' : 's'}`}
                        onClick={() => onChange(n === value ? 0 : n)}
                        className={`w-11 h-11 rounded-lg border text-[24px] leading-none flex items-center justify-center transition-colors ${on ? 'bg-amber-50 border-amber-300 text-amber-500' : 'bg-white border-slate-200 text-slate-300'}`}
                    >
                        <span aria-hidden="true">★</span>
                    </button>
                );
            })}
            <span className="ml-1 text-xs text-slate-500 tabular-nums">{value > 0 ? `${value}/5` : 'Not rated'}</span>
        </div>
    );
}

export default function RecapFlow({ job, clientName, jobCrew, materialTypes, materialsTaxRatePct, dayHoursSaved, waivers, waiverTypes, token, step, onStep, onExit, onSubmitted, onChanged, strip }) {
    const { show } = useToast();
    const materialsCatalog = useMemo(() => materialTypes || [], [materialTypes]);
    const sections = useMemo(() => recapSectionsFor(job.type, job.phase_kind), [job.type, job.phase_kind]);
    const isPackDay = job.type === 'PACKING' || kindIsPack(job.phase_kind);
    const submittedAt = job.recap_submitted_at;

    // ── Step 1 · Times ──────────────────────────────────────────────────
    const [start, setStart] = useState(job.actual_start_time || job.time || '');
    const [end, setEnd] = useState(job.actual_end_time || '');
    // Lunch break (per job, billing). Deducts from billed hours live and
    // seeds the crew-wide break in the Hours tab. Defaults to the saved
    // value, else 30. Once a Crew Hours row exists the DAY PANEL owns the
    // break (QA M5 BUG-1): read-only here, synced to the job's value.
    const [lunch, setLunch] = useState(
        Number.isFinite(Number(job.actual_lunch_minutes)) ? Number(job.actual_lunch_minutes) : 30,
    );
    useEffect(() => {
        if (dayHoursSaved) {
            const v = Number(job.actual_lunch_minutes);
            setLunch(Number.isFinite(v) ? v : 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dayHoursSaved, job.actual_lunch_minutes]);
    const computedHours = useMemo(() => hoursBetween(start, end, lunch), [start, end, lunch]);

    // ── Step 2 · Rating & notes ─────────────────────────────────────────
    const [rating, setRating] = useState(job.customer_rating || 0);
    const [crewNotes, setCrewNotes] = useState(job.crew_recap_notes || '');
    const [damageNotes, setDamageNotes] = useState(job.damage_notes || '');

    // ── Step 3 · Tip ────────────────────────────────────────────────────
    // Splits map crew_member_id → dollar string. Default even split across
    // the crew (remainder pennies to the lead); once the lead edits one
    // amount we stop re-evening — they're driving the split.
    const crewListForTips = useMemo(() => (jobCrew || []).filter(c => c && c.id), [jobCrew]);
    const [tipOpen, setTipOpen] = useState(job.tip_total != null && Number(job.tip_total) > 0);
    const [tipTotal, setTipTotal] = useState(job.tip_total != null ? String(job.tip_total) : '');
    const [tipType, setTipType] = useState(job.tip_type || 'cash');
    const [splitsTouched, setSplitsTouched] = useState(!!(job.tip_splits && Object.keys(job.tip_splits).length > 0));
    const [splits, setSplits] = useState(() => {
        const initial = {};
        if (job.tip_splits && typeof job.tip_splits === 'object') {
            for (const [k, v] of Object.entries(job.tip_splits)) initial[k] = String(v);
        }
        return initial;
    });
    const evenSplit = useCallback((totalDollars, crewList) => {
        const out = {};
        const n = crewList.length;
        if (!Number.isFinite(totalDollars) || totalDollars <= 0 || n === 0) {
            for (const c of crewList) out[c.id] = '';
            return out;
        }
        const totalCents = Math.round(totalDollars * 100);
        const baseCents = Math.floor(totalCents / n);
        const remainder = totalCents - baseCents * n;
        const leadIdx = Math.max(0, crewList.findIndex(c => c.id === job.leader_id));
        crewList.forEach((c, i) => {
            const cents = baseCents + (i === leadIdx ? remainder : 0);
            out[c.id] = (cents / 100).toFixed(2);
        });
        return out;
    }, [job.leader_id]);
    useEffect(() => {
        if (splitsTouched) return;
        setSplits(evenSplit(Number(tipTotal), crewListForTips));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipTotal, crewListForTips.length]);
    const splitsSum = useMemo(() => {
        let cents = 0;
        for (const c of crewListForTips) {
            const v = Number(splits[c.id]);
            if (Number.isFinite(v)) cents += Math.round(v * 100);
        }
        return cents / 100;
    }, [splits, crewListForTips]);
    const tipTotalNum = useMemo(() => { const n = Number(tipTotal); return Number.isFinite(n) ? n : 0; }, [tipTotal]);
    const splitsMismatch = tipTotalNum > 0 && Math.abs(splitsSum - tipTotalNum) > 0.01;
    const handleSplitChange = (crewId, raw) => { setSplitsTouched(true); setSplits(prev => ({ ...prev, [crewId]: raw })); };
    const resetSplitsToEven = () => { setSplitsTouched(false); setSplits(evenSplit(tipTotalNum, crewListForTips)); };
    const clearTip = () => { setTipTotal(''); setTipType('cash'); setSplits({}); setSplitsTouched(false); setTipOpen(false); };
    const tipChoice = tipOpen ? tipType : 'none';
    const pickTip = (v) => {
        if (v === 'none') { clearTip(); return; }
        setTipOpen(true);
        setTipType(v);
    };

    // ── Step 3 · Payment (sql/043) + packing billing (sql/045) ──────────
    const [paymentType, setPaymentType] = useState(job.payment_type || 'cash');
    const [paymentAmount, setPaymentAmount] = useState(job.payment_amount != null ? String(job.payment_amount) : '');
    const [packingBillingMode, setPackingBillingMode] = useState(job.packing_billing_mode || null);
    const paymentSectionGated = sections.has('packing_billing') && packingBillingMode === 'with_move_billing';
    const showPayment = sections.has('payment') && !paymentSectionGated;
    const showCheckPhoto = showPayment && paymentType === 'check';

    // ── Step 4 · Materials (sql/044) + photos ───────────────────────────
    const [materialsCounts, setMaterialsCounts] = useState(() => {
        const m = {};
        try {
            for (const it of (job.materials_used?.items || [])) if (it?.id) m[it.id] = Number(it.count) || 0;
        } catch { /* ignore */ }
        return m;
    });
    const setMaterialCount = (id, value) => {
        const n = Math.max(0, Math.floor(Number(value) || 0));
        setMaterialsCounts(prev => {
            const next = { ...prev };
            if (n === 0) delete next[id]; else next[id] = n;
            return next;
        });
    };
    const materialLines = useMemo(() => materialsCatalog
        .map(mt => ({ mt, count: Number(materialsCounts[mt.id]) || 0 }))
        .filter(l => l.count > 0), [materialsCatalog, materialsCounts]);
    const materialsTotals = useMemo(() => {
        const ratePct = Number.isFinite(Number(materialsTaxRatePct)) ? Number(materialsTaxRatePct) : 6.75;
        const subtotal = Math.round(materialLines.reduce((s, l) => s + l.count * (Number(l.mt.price) || 0), 0) * 100) / 100;
        const tax = Math.round(subtotal * ratePct) / 100;
        return { ratePct, subtotal, tax, total: Math.round((subtotal + tax) * 100) / 100 };
    }, [materialLines, materialsTaxRatePct]);

    const photoError = useCallback((msg) => show(msg, 'error'), [show]);
    const bolPhoto = usePhotoCapture(photoError);
    const checkPhoto = usePhotoCapture(photoError);
    const existingBolPhotoUrl = job.bol_photo_url || null;
    const existingCheckPhotoUrl = job.payment_check_photo_url || null;

    const [submitting, setSubmitting] = useState(false);
    const [tried, setTried] = useState(() => new Set());

    // ── Per-step validation (short messages) ────────────────────────────
    const errorsFor = useCallback((n) => {
        const errs = [];
        if (n === 1) {
            if (!start) errs.push({ key: 'start', label: 'Billing start', msg: 'Required' });
            if (!end) errs.push({ key: 'end', label: 'Billing end', msg: 'Required' });
            else if (start && timeToMin(end) <= timeToMin(start)) errs.push({ key: 'end', label: 'Billing end', msg: 'After start' });
        }
        if (n === 3) {
            if (tipOpen && !(tipTotalNum > 0)) errs.push({ key: 'tip', label: 'Tip amount', msg: 'Required' });
            if (tipOpen && tipType === 'cash' && tipTotalNum > 0 && splitsMismatch) errs.push({ key: 'splits', label: 'Splits', msg: "Don't match tip" });
        }
        return errs;
    }, [start, end, tipOpen, tipTotalNum, tipType, splitsMismatch]);
    const stepErrors = errorsFor(step);
    const showErrors = tried.has(step);
    const err = (key) => (showErrors ? stepErrors.find(e => e.key === key)?.msg : undefined);
    const markTried = (n) => setTried(prev => { const s = new Set(prev); s.add(n); return s; });

    const goNext = () => {
        if (stepErrors.length > 0) { markTried(step); return; }
        onStep(Math.min(5, step + 1));
    };
    const goBack = () => { if (step > 1) onStep(step - 1); else onExit(); };

    // ── The ONE save ────────────────────────────────────────────────────
    const submit = async () => {
        // Everything must pass before the payload is built.
        const all = [1, 2, 3, 4].map(n => ({ n, errs: errorsFor(n) })).filter(x => x.errs.length);
        if (all.length) {
            setTried(prev => { const s = new Set(prev); all.forEach(x => s.add(x.n)); s.add(5); return s; });
            return;
        }
        setSubmitting(true);
        try {
            // Encode any photos the lead attached. Each is optional — the
            // server skips photo handling for missing slots.
            const encodePhoto = async (file, fallbackName) => {
                if (!file) return [null, null];
                try { return [await fileToBase64(file), file.name || fallbackName]; }
                catch (e) { console.warn('base64 encode failed:', e); return [null, null]; }
            };
            const [bol_photo_base64, bol_photo_filename] = await encodePhoto(bolPhoto.file, 'bol.jpg');
            const [check_photo_base64, check_photo_filename] = await encodePhoto(checkPhoto.file, 'check.jpg');
            const paymentAmountNum = paymentAmount === '' ? null : Number(paymentAmount);

            // Token rides in the body; JWT auth header is added by the queue
            // at sync time (fresh, so a queued recap re-auths cleanly).
            const payload = {
                action: 'submit_recap',
                token: token || undefined,
                jobId: job.id,
                actual_start_time: start || null,
                actual_end_time: end || null,
                actual_hours: computedHours,
                // Once a Crew Hours row exists the day panel is the ONLY
                // writer of the billing break (QA M5 BUG-1) — omitting the
                // field leaves the job's stored value untouched server-side.
                actual_lunch_minutes: dayHoursSaved ? undefined : (Number(lunch) || 0),
                crew_recap_notes: crewNotes || null,
                damage_notes: damageNotes || null,
                customer_rating: rating || null,
                payment_type: paymentAmountNum != null && paymentAmountNum > 0 ? paymentType : undefined,
                payment_amount: paymentAmountNum != null && paymentAmountNum > 0 ? paymentAmountNum : undefined,
                packing_billing_mode: sections.has('packing_billing') ? packingBillingMode : undefined,
                check_photo_base64: paymentType === 'check' ? check_photo_base64 : null,
                check_photo_filename: paymentType === 'check' ? check_photo_filename : null,
                bol_photo_base64: sections.has('bol_photo') ? bol_photo_base64 : null,
                bol_photo_filename: sections.has('bol_photo') ? bol_photo_filename : null,
                materials_used: sections.has('materials')
                    ? {
                        items: materialsCatalog
                            .map(mt => {
                                const count = Number(materialsCounts[mt.id]) || 0;
                                if (count <= 0) return null;
                                return { id: mt.id, name: mt.name, unit: mt.unit, count };
                            })
                            .filter(Boolean),
                    }
                    : undefined,
                tip_total: tipOpen && tipTotalNum > 0 ? tipTotalNum : null,
                tip_type: tipOpen && tipTotalNum > 0 ? tipType : null,
                tip_splits: tipOpen && tipTotalNum > 0 && tipType === 'cash'
                    ? Object.fromEntries(
                        crewListForTips
                            .map(c => [c.id, Number(splits[c.id]) || 0])
                            .filter(([, v]) => v > 0),
                    )
                    : {},
            };

            // Durable offline queue: saved to device first, synced now if
            // online (else when signal returns). Deduped by job so a re-save
            // replaces the pending one — no duplicate photo uploads.
            const { outcome, body, error } = await enqueueAndSync({
                key: `recap:${job.id}`,
                action: 'submit_recap',
                payload,
                label: 'Recap',
            });

            if (outcome === 'failed') {
                show(error || 'Save failed', 'error');
            } else if (outcome === 'synced') {
                const responseBody = body || {};
                const photoErrs = responseBody.photo_errors || (responseBody.photo_error ? { recap: responseBody.photo_error } : null);
                if (photoErrs) {
                    const labels = { recap: 'reset truck photo', bol: 'BOL photo', check: 'check photo' };
                    const failedList = Object.keys(photoErrs).map(k => labels[k] || k).join(', ');
                    show(`Recap saved, but these uploads failed: ${failedList}`, 'error', { description: 'Open Edit recap and re-take them.' });
                } else {
                    show(submittedAt ? 'Recap updated' : 'Recap submitted', 'success');
                }
                if (responseBody.bol_photo_url) bolPhoto.clear();
                if (responseBody.payment_check_photo_url) checkPhoto.clear();
                onChanged?.();
                onSubmitted?.({ queued: false });
            } else {
                // offline / transient server error — safely queued on device.
                onSubmitted?.({ queued: true });
            }
        } catch (e) {
            show(e?.message || 'Save failed', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Screens ─────────────────────────────────────────────────────────
    const bannerItems = showErrors ? stepErrors.map(e => `${e.label} — ${e.msg.toLowerCase()}`) : [];

    const renderTimes = () => (
        <>
            <MobileCard title="Billing window">
                <div className="grid grid-cols-2 gap-3">
                    <FormField label="Billing start" className="mb-0" error={err('start')}>
                        <TimePicker value={start} onChange={setStart} ariaLabel="Billing start time" />
                    </FormField>
                    <FormField label="Billing end" className="mb-0" error={err('end')}>
                        <TimePicker value={end} onChange={setEnd} ariaLabel="Billing end time" />
                    </FormField>
                </div>
            </MobileCard>
            <MobileCard title="Lunch break">
                <Slider
                    value={lunch}
                    onChange={n => setLunch(n)}
                    min={0} max={90} step={15}
                    format={v => `${v} min`}
                    disabled={dayHoursSaved}
                    ariaLabel="Lunch break (minutes)"
                    className={dayHoursSaved ? 'opacity-40' : ''}
                />
                <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                    {dayHoursSaved
                        ? 'Breaks are managed on the Hours tab now — adjust them there.'
                        : 'Deducts from billed hours and seeds everyone’s break on the Hours tab.'}
                </p>
            </MobileCard>
            <div className="flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-blue-50 border border-blue-100">
                <div className="min-w-0">
                    <span className="text-xs font-bold tracking-wider text-blue-900 uppercase block">Billed hours</span>
                    <span className="text-[11px] text-blue-700/80 leading-snug">{lunch > 0 ? `Billing window minus ${lunch} min lunch` : 'Billing window'}</span>
                </div>
                <span className="text-lg font-bold text-blue-900 flex-shrink-0 tabular-nums">{computedHours === null ? '—' : `${computedHours}h`}</span>
            </div>
        </>
    );

    const renderRating = () => (
        <>
            <MobileCard title="Customer satisfaction (internal)">
                <StarRating value={rating} onChange={setRating} />
            </MobileCard>
            <MobileCard title="Crew notes">
                <Textarea value={crewNotes} onChange={e => setCrewNotes(e.target.value)} placeholder="How did it go? Anything dispatch should know?" rows={4} className="text-[15px]" />
            </MobileCard>
            <MobileCard title="Damage notes">
                <Textarea value={damageNotes} onChange={e => setDamageNotes(e.target.value)} placeholder="Any damaged items, walls, etc.?" rows={3} className="text-[15px]" />
            </MobileCard>
        </>
    );

    const renderTipPayment = () => (
        <>
            <MobileCard title="Tip received">
                <ChoiceKeys items={TIP_ITEMS} value={tipChoice} onChange={pickTip} columns={4} ariaLabel="Tip type" />
                {tipOpen && (
                    <div className="mt-3 space-y-3">
                        <FormField label="Amount" className="mb-0" error={err('tip')}>
                            <NumberField value={tipTotal} onChange={v => setTipTotal(v)} step={5} min={0} prefix="$" placeholder="0.00" ariaLabel="Tip amount" />
                        </FormField>
                        {/* CASH: lead enters per-member splits (cash is handed
                            out on-site). CARD/CHECK: auto-split on payroll by
                            hours worked across the trip. */}
                        {tipType === 'cash' && crewListForTips.length > 0 && tipTotalNum > 0 && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">Split across crew</span>
                                    {splitsTouched && <Button variant="tertiary" size="sm" onClick={resetSplitsToEven}>Reset to even</Button>}
                                </div>
                                {crewListForTips.map(c => (
                                    <div key={c.id} className="flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: c.display_color || '#6b7280' }} aria-hidden="true" />
                                        <span className="flex-1 text-[13px] font-semibold text-slate-800 truncate">{c.name}{c.id === job.leader_id ? ' (L)' : ''}</span>
                                        <NumberField value={splits[c.id] || ''} onChange={v => handleSplitChange(c.id, v)} step={1} min={0} prefix="$" placeholder="0.00" ariaLabel={`${c.name} tip share`} className="w-[150px] flex-shrink-0" />
                                    </div>
                                ))}
                                <div className={`flex items-center justify-between text-[12px] px-3 py-2 rounded-lg border ${splitsMismatch ? 'bg-red-50 border-red-200 text-red-800' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
                                    <span>Split total</span>
                                    <span className="font-bold tabular-nums">{money(splitsSum)} of {money(tipTotalNum)}</span>
                                </div>
                                <FieldError>{err('splits')}</FieldError>
                            </div>
                        )}
                        {tipType === 'cash' && tipTotalNum > 0 && (
                            <p className="text-[11px] text-slate-500 leading-snug">Cash tips are tracked but don't go through payroll — assume each person already has theirs.</p>
                        )}
                        {tipType !== 'cash' && tipTotalNum > 0 && (
                            <p className="text-[11px] text-slate-500 leading-snug">{cap(tipType)} tips are split on payroll by hours worked.</p>
                        )}
                    </div>
                )}
            </MobileCard>

            {/* PACKING-only — did the pack-day lead collect today, or will
                the packing fee be billed with the move? "With the move"
                hides Payment collected since nothing's collected today. */}
            {sections.has('packing_billing') && (
                <MobileCard title="Packing billing">
                    <ChoiceKeys items={PACKING_ITEMS} value={packingBillingMode || ''} onChange={v => setPackingBillingMode(v)} columns={1} ariaLabel="Packing billing mode" />
                    <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                        {packingBillingMode === 'with_move_billing'
                            ? 'No money today — the packing fee rolls into the move recap.'
                            : packingBillingMode === 'collected_today'
                                ? 'Lead took payment on pack day.'
                                : 'Pick one so the office knows where the packing fee lands.'}
                    </p>
                </MobileCard>
            )}

            {showPayment && (
                <MobileCard title="Payment collected" right={(job.payment_type && job.payment_amount > 0) ? <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md uppercase bg-blue-100 text-blue-700">On file</span> : null}>
                    <FormField label="Method" className="mb-3">
                        <ChoiceKeys items={PAY_ITEMS} value={paymentType} onChange={v => setPaymentType(v)} columns={3} ariaLabel="Payment method" />
                    </FormField>
                    <FormField label="Amount" className="mb-0">
                        <NumberField value={paymentAmount} onChange={v => setPaymentAmount(v)} step={5} min={0} prefix="$" placeholder="0.00" ariaLabel="Payment amount" />
                    </FormField>
                    <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">Leave the amount blank if nothing was collected today.{paymentType === 'check' ? ' You’ll snap the check on the next step.' : ''}</p>
                </MobileCard>
            )}
        </>
    );

    const renderMaterialsPhotos = () => (
        <>
            {sections.has('materials') && (
                <MobileCard title={isPackDay ? '📦 Materials — pack day' : 'Materials used'} className={isPackDay ? 'border-emerald-300' : ''}>
                    {isPackDay && (
                        <p className="text-[11px] text-emerald-700 leading-snug mb-2">Count everything used today — boxes, paper, tape. This is what the office bills the packing from.</p>
                    )}
                    {materialsCatalog.length === 0 ? (
                        <p className="text-[12px] text-slate-500">No materials configured yet. Ask the office to add the catalog under Settings → Materials.</p>
                    ) : (
                        <div className="space-y-1.5">
                            {materialsCatalog.map(mt => {
                                const count = Number(materialsCounts[mt.id]) || 0;
                                return (
                                    <div key={mt.id} className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 border ${count > 0 ? 'bg-emerald-50/50 border-emerald-300' : 'bg-white border-slate-200'}`}>
                                        <span className="flex-1 text-[13px] font-semibold text-slate-800 truncate">
                                            {mt.name} <span className="text-[10px] font-medium text-slate-400">({mt.unit})</span>
                                        </span>
                                        <NumberField value={count || ''} onChange={v => setMaterialCount(mt.id, v)} step={1} min={0} placeholder="0" ariaLabel={`${mt.name} count`} inputClassName="font-bold" className="w-[136px] flex-shrink-0" />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {/* Subtotal / Tax / Total — customer-facing prices only;
                        the server re-computes + snapshots at submit. */}
                    {materialLines.length > 0 && (
                        <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2">
                            <KV label="Materials subtotal" value={money(materialsTotals.subtotal)} mono className="py-0.5" />
                            <KV label={`Sales tax (${materialsTotals.ratePct}%)`} value={money(materialsTotals.tax)} mono className="py-0.5" />
                            <div className="flex justify-between text-[13px] font-bold text-emerald-800 pt-1 mt-0.5 border-t border-emerald-200"><span>Collect for materials</span><span className="font-mono tabular-nums">{money(materialsTotals.total)}</span></div>
                        </div>
                    )}
                </MobileCard>
            )}

            {/* BOL — LOCAL + LONG_DISTANCE issue a Bill of Lading; the office
                gets the paperwork without waiting for the truck. */}
            {sections.has('bol_photo') && (
                <MobileCard title={job.type === 'LONG_DISTANCE' ? 'Long-distance BOL photo' : 'Local BOL photo'}>
                    <PhotoCaptureField
                        capture={bolPhoto}
                        existingUrl={existingBolPhotoUrl}
                        emptyLabel={`📄 Add ${job.type === 'LONG_DISTANCE' ? 'long-distance' : 'local'} BOL photo`}
                        emptyHint="Snap the completed Bill of Lading so the office has the paperwork right away."
                        previewAlt="BOL"
                        accent="amber"
                    />
                </MobileCard>
            )}

            {showCheckPhoto && (
                <MobileCard title="Check photo">
                    <PhotoCaptureField
                        capture={checkPhoto}
                        existingUrl={existingCheckPhotoUrl}
                        emptyLabel="🧾 Add check photo"
                        emptyHint="Snap the check so the office can deposit it."
                        previewAlt="Check"
                        accent="blue"
                    />
                </MobileCard>
            )}

            {sections.has('reset_truck_photo') && (
                <p className="px-1 text-[11px] text-slate-500 leading-snug">The truck reset photo is one per day — it lives on the End of day tab.</p>
            )}

            <MobileCard title={`Waivers (${(waivers || []).length})`}>
                {(waivers || []).length === 0 ? (
                    <p className="text-xs text-slate-400">No waivers on file. Add one from the job screen if the customer needs to sign something.</p>
                ) : (
                    <ul className="space-y-1">
                        {waivers.map(w => {
                            const t = (waiverTypes || []).find(x => x.key === w.waiver_type);
                            return (
                                <li key={w.id} className="flex items-center justify-between gap-2 text-[13px]">
                                    <span className="font-semibold text-slate-800 truncate">{t ? (t.shortLabel || t.title || w.waiver_type) : w.waiver_type}</span>
                                    <span className={`text-[11px] font-semibold flex-shrink-0 ${w.signed_at ? 'text-emerald-700' : 'text-amber-700'}`}>{w.signed_at ? 'Signed' : 'Pending'}</span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </MobileCard>
        </>
    );

    const editBtn = (n) => <Button variant="tertiary" size="sm" onClick={() => onStep(n)}>Edit</Button>;
    const renderReview = () => (
        <>
            <MobileCard title="1 · Times" right={editBtn(1)}>
                <div className="divide-y divide-slate-100">
                    <KV label="Billing start" value={formatTime12(start)} mono />
                    <KV label="Billing end" value={formatTime12(end)} mono />
                    <KV label="Lunch" value={`${lunch} min`} mono />
                    <KV label="Billed hours" value={computedHours === null ? null : `${computedHours}h`} mono />
                </div>
            </MobileCard>
            <MobileCard title="2 · Rating & notes" right={editBtn(2)}>
                <div className="divide-y divide-slate-100">
                    <KV label="Rating" value={rating > 0 ? `${'★'.repeat(rating)} ${rating}/5` : 'Not rated'} />
                    <KV label="Crew notes" value={crewNotes || null} />
                    <KV label="Damage notes" value={damageNotes || 'None'} />
                </div>
            </MobileCard>
            <MobileCard title="3 · Tip & payment" right={editBtn(3)}>
                <div className="divide-y divide-slate-100">
                    <KV label="Tip" value={tipOpen && tipTotalNum > 0 ? `${money(tipTotalNum)} ${tipType}` : 'None'} mono />
                    {tipOpen && tipTotalNum > 0 && tipType === 'cash' && crewListForTips.map(c => (
                        <KV key={c.id} label={`↳ ${c.name}`} value={money(splits[c.id])} mono />
                    ))}
                    {sections.has('packing_billing') && (
                        <KV label="Packing billing" value={packingBillingMode === 'collected_today' ? 'Collected today' : packingBillingMode === 'with_move_billing' ? "With the move's billing" : null} />
                    )}
                    {showPayment && (
                        <KV label="Payment collected" value={Number(paymentAmount) > 0 ? `${money(paymentAmount)} ${paymentType}` : 'Nothing today'} mono />
                    )}
                </div>
            </MobileCard>
            <MobileCard title="4 · Materials & photos" right={editBtn(4)}>
                <div className="divide-y divide-slate-100">
                    {sections.has('materials') && (
                        materialLines.length === 0
                            ? <KV label="Materials" value="None" />
                            : (
                                <>
                                    {materialLines.map(l => <KV key={l.mt.id} label={l.mt.name} value={`${l.count} ${l.mt.unit}`} mono />)}
                                    <KV label="Collect for materials" value={money(materialsTotals.total)} mono />
                                </>
                            )
                    )}
                    {sections.has('bol_photo') && (
                        <KV label="BOL photo" value={bolPhoto.file ? 'Attached' : existingBolPhotoUrl ? 'On file' : 'None'} />
                    )}
                    {showCheckPhoto && (
                        <KV label="Check photo" value={checkPhoto.file ? 'Attached' : existingCheckPhotoUrl ? 'On file' : 'None'} />
                    )}
                    <KV label="Waivers" value={(waivers || []).length ? `${waivers.filter(w => w.signed_at).length} signed · ${waivers.filter(w => !w.signed_at).length} pending` : 'None'} />
                </div>
            </MobileCard>
            {submittedAt && (
                <p className="px-1 text-[11px] text-slate-500 text-center leading-snug">This recap was already submitted — Submit again to update it.</p>
            )}
        </>
    );

    const body = step === 1 ? renderTimes() : step === 2 ? renderRating() : step === 3 ? renderTipPayment() : step === 4 ? renderMaterialsPhotos() : renderReview();

    return (
        <MobileScreen
            header={(
                <MobileHeader
                    title={`Recap · ${clientName}`}
                    sub={`Step ${step} of 5 · ${STEP_NAMES[step]}`}
                    onBack={goBack}
                />
            )}
            strip={<>{strip}<StepBar count={5} value={step} /></>}
            action={(
                <MobileActionBar>
                    <Button variant="tertiary" size="lg" onClick={goBack} disabled={submitting}>Back</Button>
                    {step < 5
                        ? <Button size="lg" full className="flex-1" onClick={goNext}>Next →</Button>
                        : <Button size="lg" full className="flex-1" loading={submitting} onClick={submit}>{submittedAt ? 'Update recap' : 'Submit recap'}</Button>}
                </MobileActionBar>
            )}
        >
            {step === 5 && tried.has(5) && (() => {
                const items = [1, 2, 3, 4].flatMap(n => errorsFor(n).map(e => `Step ${n} · ${e.label} — ${e.msg.toLowerCase()}`));
                return items.length ? <FormErrorBanner title="Fix these before submitting" items={items} /> : null;
            })()}
            {step < 5 && <FormErrorBanner title={bannerItems.length === 1 ? 'Fix 1 thing before moving on' : `Fix ${bannerItems.length} things before moving on`} items={bannerItems} />}
            {body}
        </MobileScreen>
    );
}
